import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import axios from 'axios';
import {
  FaArrowLeft,
  FaTicketAlt,
  FaCloud,
  FaEnvelope,
  FaUsers,
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaSpinner,
  FaPaperclip,
  FaRegCopy,
  FaCheck,
  FaTimes,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFilePdf,
  FaEye,
  FaTrash,
  FaEdit,
  FaExclamationCircle,
} from 'react-icons/fa';
import { FiRefreshCw, FiUploadCloud, FiAlertTriangle } from 'react-icons/fi';
import { AiOutlineSend } from 'react-icons/ai';

const DEFAULT_CLOUD_PRODUCTS = [
  'Google Cloud Platform (GCP)',
  'Compute Engine (VM)',
  'Google Kubernetes Engine (GKE)',
  'Cloud Run',
  'Cloud Functions',
  'Cloud Storage',
  'Cloud SQL (PostgreSQL / MySQL)',
  'BigQuery & Analytics',
  'Cloud Firestore / Datastore',
  'Cloud Pub/Sub',
  'VPC Network & Cloud Interconnect',
  'Cloud Load Balancing',
  'Cloud IAM & Security',
  'AWS Cloud Services',
  'Microsoft Azure Cloud Services',
  'Google Cloud Managed Services',
];

const PRIORITY_CONFIG = {
  'P1 - Critical': {
    label: 'P1 - Critical',
    color: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800',
    sla: 'SLA < 1 Jam',
  },
  'P2 - High impact': {
    label: 'P2 - High Impact',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800',
    sla: 'SLA < 4 Jam',
  },
  'P3 - Medium impact': {
    label: 'P3 - Medium Impact',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800',
    sla: 'SLA < 8 Jam',
  },
  'P4 - Low impact': {
    label: 'P4 - Low Impact',
    color: 'bg-gray-50 text-gray-700 border-gray-200',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-800',
    sla: 'SLA < 24 Jam',
  },
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const parseAttachmentUrls = (attachmentStr) => {
  if (!attachmentStr) return [];
  const s = String(attachmentStr).trim();
  if (!s || s.toLowerCase() === 'none' || s.toLowerCase() === 'null') return [];
  if (s.includes('|||')) {
    return s.split('|||').map((u) => u.trim()).filter(Boolean);
  }
  if (s.startsWith('[') && s.endsWith(']')) {
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr)) return arr.map((u) => String(u).trim()).filter(Boolean);
    } catch (e) {}
  }
  return s.split(/,(?=\s*https?:\/\/)/).map((u) => u.trim()).filter(Boolean);
};

export default function TicketDetail() {
  const { ticket_id } = useParams();
  const navigate = useNavigate();

  // User & role info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const role = user.role || '';
  const userId = user.id_user || user.id || user.username || user.email || '';
  // Customer Admin and Customer should not be able to edit or delete tickets
  const isAdmin = role === 'Admin' || role === 'Support';

  // Layout state
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Ticket data state
  const [ticket, setTicket] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(true);
  const [ticketError, setTicketError] = useState(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isDraggingCommentFiles, setIsDraggingCommentFiles] = useState(false);
  const chatBottomRef = useRef(null);
  const commentFileInputRef = useRef(null);

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState(false);

  // Lightbox Preview Modal
  const [previewFile, setPreviewFile] = useState(null);

  // Edit Ticket Modal (for Admin)
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);
  const [editForm, setEditForm] = useState({
    product_list: '',
    describe_issue: '',
    detail_issue: '',
    priority: '',
    contact: '',
    status: '',
    cc_emails: '',
  });

  // Delete Ticket Modal (for Admin)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingTicket, setIsDeletingTicket] = useState(false);

  // Status updating inline
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Error Alert Modal
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: '',
  });

  // -------------------------
  // FETCH TICKET & COMMENTS
  // -------------------------
  const fetchTicket = useCallback(async () => {
    if (!ticket_id) return;
    setLoadingTicket(true);
    setTicketError(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${ticket_id}`
      );
      if (res.data) {
        setTicket(res.data);
      } else {
        setTicketError('Ticket data not found.');
      }
    } catch (err) {
      console.error('Failed to load ticket:', err);
      if (err.response?.status === 404) {
        setTicketError('Ticket not found.');
      } else {
        setTicketError(
          err.response?.data?.detail || 'Failed to load ticket details. Please try again.'
        );
      }
    } finally {
      setLoadingTicket(false);
    }
  }, [ticket_id]);

  const fetchComments = useCallback(
    async (silent = false) => {
      if (!ticket_id) return;
      if (!silent) setLoadingComments(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/tickets/comment/${ticket_id}`
        );
        if (Array.isArray(res.data)) {
          setComments(res.data);
        }
      } catch (err) {
        console.error('Failed to load comments:', err);
      } finally {
        if (!silent) setLoadingComments(false);
      }
    },
    [ticket_id]
  );

  useEffect(() => {
    fetchTicket();
    fetchComments();
  }, [fetchTicket, fetchComments]);

  // Scroll to bottom of comments on new message
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments]);

  // -------------------------
  // COPY TICKET ID
  // -------------------------
  const handleCopyId = () => {
    if (!ticket?.ticket_id) return;
    navigator.clipboard.writeText(ticket.ticket_id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // -------------------------
  // INLINE STATUS CHANGE (Admin / Support)
  // -------------------------
  const handleStatusChange = async (newStatus) => {
    if (!isAdmin || !ticket || isUpdatingStatus || ticket.status === newStatus) return;
    setIsUpdatingStatus(true);
    try {
      const payload = {
        product_list: ticket.product_list,
        describe_issue: ticket.describe_issue,
        detail_issue: ticket.detail_issue,
        priority: ticket.priority,
        contact: ticket.contact,
        status: newStatus,
        cc_emails: ticket.cc_emails || '',
      };
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${ticket.ticket_id}`,
        payload
      );
      setTicket((prev) => ({ ...prev, status: newStatus }));
      fetchComments(true);
    } catch (err) {
      console.error('Failed to update ticket status:', err);
      setErrorModal({
        isOpen: true,
        title: 'Status Update Failed',
        message: err.response?.data?.detail || 'Failed to update ticket status.',
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // -------------------------
  // EDIT TICKET
  // -------------------------
  const handleOpenEdit = () => {
    if (!isAdmin || !ticket) return;
    setEditForm({
      product_list: ticket.product_list || DEFAULT_CLOUD_PRODUCTS[0],
      describe_issue: ticket.describe_issue || '',
      detail_issue: ticket.detail_issue || '',
      priority: ticket.priority || 'P3 - Medium impact',
      contact: ticket.contact || '',
      status: ticket.status || 'Open',
      cc_emails: ticket.cc_emails || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!isAdmin || !ticket || isUpdatingTicket) return;
    setIsUpdatingTicket(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${ticket.ticket_id}`,
        editForm
      );
      setTicket((prev) => ({ ...prev, ...editForm }));
      setShowEditModal(false);
      fetchComments(true);
    } catch (err) {
      console.error('Failed to update ticket:', err);
      setErrorModal({
        isOpen: true,
        title: 'Update Failed',
        message: err.response?.data?.detail || 'Failed to update ticket.',
      });
    } finally {
      setIsUpdatingTicket(false);
    }
  };

  // -------------------------
  // DELETE TICKET
  // -------------------------
  const handleDeleteTicket = async () => {
    if (!isAdmin || !ticket || isDeletingTicket) return;
    setIsDeletingTicket(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/${ticket.ticket_id}`
      );
      setShowDeleteModal(false);
      navigate('/support-request', { replace: true });
    } catch (err) {
      console.error('Failed to delete ticket:', err);
      setErrorModal({
        isOpen: true,
        title: 'Delete Failed',
        message: err.response?.data?.detail || 'Failed to delete ticket.',
      });
    } finally {
      setIsDeletingTicket(false);
    }
  };

  // -------------------------
  // COMMENT FILES & DRAG-N-DROP
  // -------------------------
  const processCommentFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || []);
    if (!files.length) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    const oversized = files.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setErrorModal({
        isOpen: true,
        title: 'File Size Exceeded (Max 5 MB)',
        message: `The following file(s) exceed the 5 MB limit:\n${oversized
          .map((f) => `• ${f.name} (${formatFileSize(f.size)})`)
          .join('\n')}\n\nPlease choose files under 5 MB.`,
      });
      return;
    }

    setCommentFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const freshFiles = files.filter((f) => !existingKeys.has(`${f.name}-${f.size}`));
      return [...prev, ...freshFiles];
    });
  };

  const handleCommentFileChange = (e) => {
    if (e.target.files) {
      processCommentFiles(e.target.files);
    }
  };

  const handleCommentDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentFiles(true);
  };

  const handleCommentDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCommentDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingCommentFiles(false);
  };

  const handleCommentDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCommentFiles(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processCommentFiles(e.dataTransfer.files);
      if (e.dataTransfer.clearData) e.dataTransfer.clearData();
    }
  };

  const handleCommentPaste = (e) => {
    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) {
        processCommentFiles(files);
      }
    }
  };

  const handleRemoveCommentFile = (indexToRemove) => {
    setCommentFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSendComment = async () => {
    if (!ticket || (!commentText.trim() && commentFiles.length === 0)) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    const oversized = commentFiles.filter((f) => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      setErrorModal({
        isOpen: true,
        title: 'File Size Exceeded (Max 5 MB)',
        message: `The following file(s) exceed the 5 MB limit:\n${oversized
          .map((f) => `• ${f.name} (${formatFileSize(f.size)})`)
          .join('\n')}\n\nPlease choose files under 5 MB.`,
      });
      return;
    }

    setIsSubmittingComment(true);
    try {
      const formData = new FormData();
      formData.append('id_user', userId || 'user');
      formData.append('comment', commentText.trim());
      commentFiles.forEach((file) => {
        formData.append('attachment', file);
      });

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tickets/comment/${ticket.ticket_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setCommentText('');
      setCommentFiles([]);
      if (commentFileInputRef.current) {
        commentFileInputRef.current.value = '';
      }
      fetchComments(true);
    } catch (err) {
      console.error('Error posting comment:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = 'Failed to send comment. Please try again.';
      if (status === 413) {
        msg = 'The attached files are too large. Maximum size is 5 MB per file.';
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      setErrorModal({
        isOpen: true,
        title: 'Failed to Send Comment',
        message: msg,
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const priorityConf = ticket ? (PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG['P3 - Medium impact']) : null;
  const initialAttachments = ticket ? parseAttachmentUrls(ticket.attachment) : [];

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 font-sans">
      {/* Header */}
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 min-w-0 max-w-full overflow-x-hidden pt-16">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        {/* Main Content Area */}
        <main
          className={`flex-1 min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ease-in-out px-3 sm:px-6 lg:px-8 py-6 ${
            sidebarOpen ? 'lg:ml-64' : 'ml-0'
          } ml-0`}
        >
          <div className="max-w-7xl mx-auto space-y-6">

            {/* Breadcrumb & Navigation */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/support-request')}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-700 border border-slate-200/90 text-xs font-semibold shadow-2xs transition hover:-translate-x-0.5 cursor-pointer"
                >
                  <FaArrowLeft className="text-slate-500" />
                  <span>Back to Tickets</span>
                </button>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>/</span>
                  <Link to="/support-request" className="hover:text-blue-600 transition">
                    Support Tickets
                  </Link>
                  <span>/</span>
                  <span className="font-mono text-slate-600 font-medium">
                    {ticket_id}
                  </span>
                </div>
              </div>

              {ticket && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      fetchTicket();
                      fetchComments();
                    }}
                    disabled={loadingTicket || loadingComments}
                    className="p-2 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 hover:text-blue-600 transition shadow-2xs"
                    title="Refresh ticket data"
                  >
                    <FiRefreshCw
                      className={`w-4 h-4 ${loadingTicket || loadingComments ? 'animate-spin text-blue-600' : ''}`}
                    />
                  </button>

                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={handleOpenEdit}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-700 border border-slate-200 text-xs font-semibold transition shadow-2xs"
                      >
                        <FaEdit className="text-blue-600" />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-rose-50 text-slate-700 hover:text-rose-700 border border-slate-200 text-xs font-semibold transition shadow-2xs"
                      >
                        <FaTrash className="text-rose-500" />
                        <span>Delete</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Loading Skeleton */}
            {loadingTicket && !ticket && (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 shadow-sm space-y-4">
                <FaSpinner className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
                <p className="text-sm font-medium text-slate-600">Loading ticket details...</p>
              </div>
            )}

            {/* Error or Not Found State */}
            {!loadingTicket && ticketError && (
              <div className="bg-white rounded-3xl p-10 text-center border border-slate-200 shadow-sm space-y-4 max-w-xl mx-auto my-12">
                <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto text-2xl">
                  <FiAlertTriangle />
                </div>
                <h2 className="text-xl font-bold text-slate-800">Ticket Not Available</h2>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {ticketError}
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => navigate('/support-request')}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs transition shadow-sm"
                  >
                    Return to Support Tickets
                  </button>
                </div>
              </div>
            )}

            {/* TICKET FULL PAGE CONTENT */}
            {ticket && (
              <div className="space-y-6">
                {/* HERO HEADER CARD */}
                <div className="bg-gradient-to-r from-[#11498B] via-[#165ab0] to-[#1e3a8a] rounded-3xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden">
                  <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>

                  <div className="relative z-10 space-y-4">
                    {/* Top Row: Ticket ID + Status Badge + Actions */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2.5">
                        {/* Copyable Ticket ID */}
                        <button
                          type="button"
                          onClick={handleCopyId}
                          title="Click to copy Ticket ID"
                          className="inline-flex items-center gap-2 font-mono text-xs sm:text-sm font-bold bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-xl border border-white/20 transition cursor-pointer backdrop-blur-sm"
                        >
                          <FaTicketAlt className="text-blue-300 text-xs" />
                          <span>{ticket.ticket_id}</span>
                          {copiedId ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300 font-sans">
                              <FaCheck className="w-3 h-3 text-emerald-400" /> Copied!
                            </span>
                          ) : (
                            <FaRegCopy className="w-3 h-3 text-slate-300 opacity-70" />
                          )}
                        </button>

                        {/* Status Badge */}
                        {ticket.status === 'Open' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold bg-blue-500/20 text-blue-200 border border-blue-400/40 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
                            Open
                          </span>
                        ) : ticket.status === 'On Progress' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold bg-amber-500/25 text-amber-200 border border-amber-400/40 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
                            On Progress
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold bg-emerald-500/25 text-emerald-200 border border-emerald-400/40 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                            Closed
                          </span>
                        )}

                        {/* Priority Badge */}
                        {priorityConf && (
                          <span className="text-xs px-3 py-1 rounded-full font-medium bg-white/10 text-white/90 border border-white/15 backdrop-blur-sm">
                            {priorityConf.label}
                          </span>
                        )}
                      </div>

                      {/* Quick Status Toggle for Admin / Support */}
                      {isAdmin && (
                        <div className="flex items-center gap-2 bg-black/20 p-1 rounded-2xl border border-white/10 backdrop-blur-sm">
                          <span className="text-[11px] font-medium text-blue-200 pl-2 pr-1 hidden sm:inline">
                            Status:
                          </span>
                          {['Open', 'On Progress', 'Closed'].map((st) => (
                            <button
                              key={st}
                              type="button"
                              onClick={() => handleStatusChange(st)}
                              disabled={isUpdatingStatus}
                              className={`px-3 py-1 rounded-xl text-xs font-semibold transition cursor-pointer ${
                                ticket.status === st
                                  ? 'bg-white text-[#11498B] shadow-sm'
                                  : 'text-white/80 hover:text-white hover:bg-white/10'
                              } ${isUpdatingStatus ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              {st}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Ticket Title / Summary */}
                    <div>
                      <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-white leading-tight">
                        {ticket.describe_issue}
                      </h1>
                      <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-blue-100/80 mt-2">
                        <span className="flex items-center gap-1.5">
                          <FaCloud className="text-sky-300" />
                          <span>{ticket.product_list}</span>
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1.5">
                          <FaCalendarAlt className="text-blue-300" />
                          <span>
                            Created {ticket.created_at ? new Date(ticket.created_at).toLocaleString('en-US') : '-'}
                          </span>
                        </span>
                        {ticket.company_name && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1.5">
                              <FaBuilding className="text-blue-300" />
                              <span>{ticket.company_name}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* MAIN 2-COLUMN GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  
                  {/* LEFT COLUMN: TICKET METADATA & DETAILS (5 Cols) */}
                  <div className="lg:col-span-5 space-y-6">
                    {/* Information Card */}
                    <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-5">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Ticket Information
                        </h3>
                        <span className="text-[11px] font-semibold text-slate-400">
                          ID: {ticket.ticket_id}
                        </span>
                      </div>

                      <div className="space-y-3.5 text-xs sm:text-sm">
                        <div className="flex items-start justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-500 text-xs">Cloud Service</span>
                          <span className="font-semibold text-slate-800 text-right flex items-center gap-1.5">
                            <FaCloud className="text-sky-500" />
                            {ticket.product_list}
                          </span>
                        </div>

                        <div className="flex items-start justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-500 text-xs">Priority & SLA</span>
                          <span className="font-semibold text-slate-800 text-right">
                            {ticket.priority}
                            {priorityConf && (
                              <span className="block text-[11px] text-slate-400 font-normal">
                                {priorityConf.sla}
                              </span>
                            )}
                          </span>
                        </div>

                        <div className="flex items-start justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-500 text-xs">Contact Person</span>
                          <span className="font-medium text-slate-800 text-right truncate max-w-[220px]">
                            {ticket.contact || '-'}
                          </span>
                        </div>

                        <div className="flex items-start justify-between py-1 border-b border-slate-100">
                          <span className="text-slate-500 text-xs flex items-center gap-1">
                            <FaUsers className="text-purple-600 text-xs" />
                            <span>CC Emails</span>
                          </span>
                          <span className="font-medium text-slate-700 text-right max-w-[220px] text-xs">
                            {ticket.cc_emails || <span className="text-slate-400 italic">None</span>}
                          </span>
                        </div>

                        {ticket.company_name && (
                          <div className="flex items-start justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-500 text-xs">Company</span>
                            <span className="font-semibold text-slate-800 text-right">
                              {ticket.company_name}
                            </span>
                          </div>
                        )}

                        <div className="flex items-start justify-between py-1">
                          <span className="text-slate-500 text-xs">Created At</span>
                          <span className="font-medium text-slate-700 text-right text-xs">
                            {ticket.created_at ? new Date(ticket.created_at).toLocaleString('en-US') : '-'}
                          </span>
                        </div>
                      </div>

                      {/* Incident Report Link if present */}
                      {ticket.cm_report_url && (
                        <div className="pt-3 border-t border-slate-100">
                          <span className="text-slate-500 text-xs block mb-1.5 font-semibold">
                            Incident Report
                          </span>
                          <a
                            href={ticket.cm_report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-between w-full p-3 rounded-2xl bg-emerald-50 hover:bg-emerald-100/80 text-emerald-800 text-xs font-semibold border border-emerald-200 transition group"
                          >
                            <span className="flex items-center gap-2">
                              <FaCheckCircle className="text-emerald-600" />
                              <span>View Incident Report</span>
                            </span>
                            <FaExternalLinkAlt className="text-emerald-600 group-hover:translate-x-0.5 transition" />
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Detailed Issue Card */}
                    <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-3">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2">
                        Issue Description
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 text-xs sm:text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {ticket.detail_issue || ticket.describe_issue}
                      </div>
                    </div>

                    {/* Initial Ticket Attachments Card */}
                    <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-3">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                          Ticket Attachments
                        </h3>
                        <span className="text-xs text-slate-400">
                          {initialAttachments.length} file{initialAttachments.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {initialAttachments.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-xs bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
                          No initial files attached to this ticket
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-2.5">
                          {initialAttachments.map((url, i) => {
                            const fileName = url.split('/').pop().split('?')[0] || `Attachment ${i + 1}`;
                            const isImg = url.match(/\.(jpeg|jpg|png|gif|webp)($|\?)/i);
                            const isPdf = url.match(/\.pdf($|\?)/i);

                            return (
                              <div
                                key={i}
                                className="bg-slate-50 border border-slate-200/90 rounded-2xl p-3 flex items-center justify-between gap-3 hover:border-blue-300 transition"
                              >
                                <div
                                  className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                                  onClick={() => {
                                    if (isImg) {
                                      setPreviewFile({ url, name: fileName, isImage: true });
                                    } else {
                                      window.open(url, '_blank');
                                    }
                                  }}
                                >
                                  {isImg ? (
                                    <div className="w-11 h-11 rounded-xl overflow-hidden border border-slate-200 flex-shrink-0 bg-slate-100">
                                      <img
                                        src={url}
                                        alt={fileName}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : isPdf ? (
                                    <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
                                      <FaFilePdf className="text-red-500 text-lg" />
                                    </div>
                                  ) : (
                                    <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                                      <FaFileAlt className="text-blue-500 text-lg" />
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-slate-800 truncate" title={fileName}>
                                      {fileName}
                                    </p>
                                    <span className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                                      {isImg ? (
                                        <>
                                          <FaEye className="text-[10px]" />
                                          <span>Click to preview</span>
                                        </>
                                      ) : (
                                        <span>Open file</span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download
                                  className="p-2 text-slate-400 hover:text-blue-600 transition hover:bg-slate-100 rounded-xl"
                                  title="Download / Open file"
                                >
                                  <FaExternalLinkAlt className="text-xs" />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RIGHT COLUMN: INTERACTIVE ACTIVITY STREAM & CHAT (7 Cols) */}
                  <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200/80 shadow-sm flex flex-col h-[760px] overflow-hidden">
                    {/* Activity Header */}
                    <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                      <div>
                        <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                          Activity & Conversation
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Direct communication thread between customer and engineers
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 px-3 py-1 rounded-full shadow-2xs">
                          {comments.length} message{comments.length !== 1 ? 's' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => fetchComments(false)}
                          disabled={loadingComments}
                          className="p-2 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-white border border-transparent hover:border-slate-200 transition"
                          title="Refresh conversation"
                        >
                          <FiRefreshCw className={`w-3.5 h-3.5 ${loadingComments ? 'animate-spin text-blue-600' : ''}`} />
                        </button>
                      </div>
                    </div>

                    {/* Chat Message Stream */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50/30">
                      {loadingComments && comments.length === 0 ? (
                        <div className="text-center py-24 text-slate-400 text-xs space-y-2">
                          <FaSpinner className="animate-spin text-xl mx-auto text-blue-500" />
                          <p>Loading conversation thread...</p>
                        </div>
                      ) : comments.length === 0 ? (
                        <div className="text-center py-24 text-slate-400 text-xs max-w-sm mx-auto space-y-3">
                          <div className="w-14 h-14 rounded-3xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto text-2xl shadow-xs">
                            💬
                          </div>
                          <div>
                            <p className="font-bold text-slate-700 text-sm">No messages yet</p>
                            <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                              Send a response or update below to notify the ticket requester and team.
                            </p>
                          </div>
                        </div>
                      ) : (
                        comments.map((c, i) => {
                          const attUrls = parseAttachmentUrls(c.attachment);
                          const isMe = Boolean(
                            (userId && c.id_user === userId) ||
                            (user?.full_name && c.full_name && c.full_name.toLowerCase() === user.full_name.toLowerCase()) ||
                            (user?.email && c.id_user && c.id_user.toLowerCase() === user.email.toLowerCase())
                          );
                          const timeStr = c.timestamp
                            ? new Date(c.timestamp).toLocaleString('en-US', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '';

                          return (
                            <div
                              key={c.comment_id || i}
                              className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} gap-1.5`}
                            >
                              {/* Sender name & time */}
                              <div
                                className={`flex items-center gap-1.5 text-[11px] text-slate-400 ${
                                  isMe ? 'mr-1' : 'ml-1'
                                }`}
                              >
                                <span className="font-semibold text-slate-700">
                                  {isMe ? 'You' : c.full_name || 'Support Team'}
                                </span>
                                <span>•</span>
                                <span>{timeStr}</span>
                              </div>

                              {/* Chat Bubble */}
                              <div
                                className={`max-w-[85%] rounded-3xl p-4 shadow-2xs text-xs sm:text-sm leading-relaxed space-y-2.5 ${
                                  isMe
                                    ? 'bg-blue-600 text-white rounded-tr-xs shadow-blue-600/10'
                                    : 'bg-white text-slate-800 border border-slate-200/90 rounded-tl-xs'
                                }`}
                              >
                                {c.comment && (
                                  <p className="whitespace-pre-wrap leading-relaxed">
                                    {c.comment}
                                  </p>
                                )}

                                {/* Inline Attachment Rendering */}
                                {attUrls.length > 0 && (
                                  <div className="space-y-2 pt-1">
                                    {attUrls.map((url, attIdx) => {
                                      const fileName = url.split('/').pop().split('?')[0];
                                      const ext = fileName.split('.').pop().toLowerCase();
                                      const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);

                                      if (isImg) {
                                        return (
                                          <div
                                            key={attIdx}
                                            onClick={() => setPreviewFile({ url, name: fileName, isImage: true })}
                                            className="group relative rounded-2xl overflow-hidden border border-black/10 cursor-pointer bg-slate-900/5 max-w-sm shadow-xs"
                                          >
                                            <img
                                              src={url}
                                              alt={fileName}
                                              className="w-full max-h-60 object-cover hover:scale-[1.01] transition-transform duration-200"
                                            />
                                            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                                              <span className="bg-slate-900/70 text-white text-[11px] font-medium px-3 py-1 rounded-full flex items-center gap-1.5 backdrop-blur-xs">
                                                <FaEye className="w-3 h-3" /> Click to enlarge
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }

                                      return (
                                        <div
                                          key={attIdx}
                                          className={`flex items-center justify-between gap-2 p-2.5 rounded-2xl border text-xs ${
                                            isMe
                                              ? 'bg-blue-700/60 border-blue-400/40 text-white'
                                              : 'bg-slate-50 border-slate-200 text-slate-800'
                                          }`}
                                        >
                                          <div
                                            className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer"
                                            onClick={() => window.open(url, '_blank')}
                                          >
                                            {ext === 'pdf' ? (
                                              <FaFilePdf className={isMe ? 'text-red-200 text-base shrink-0' : 'text-red-500 text-base shrink-0'} />
                                            ) : (
                                              <FaFileAlt className={isMe ? 'text-blue-200 text-base shrink-0' : 'text-blue-500 text-base shrink-0'} />
                                            )}
                                            <span className="truncate font-medium" title={fileName}>
                                              {fileName}
                                            </span>
                                          </div>
                                          <a
                                            href={url}
                                            download={fileName}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`p-1.5 rounded-lg transition shrink-0 ${
                                              isMe
                                                ? 'text-blue-200 hover:text-white hover:bg-blue-600'
                                                : 'text-slate-400 hover:text-blue-600 hover:bg-slate-100'
                                            }`}
                                            title="Download"
                                          >
                                            <FaExternalLinkAlt className="w-3 h-3" />
                                          </a>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={chatBottomRef} />
                    </div>

                    {/* Bottom Composer Box */}
                    <div
                      onDragEnter={handleCommentDragEnter}
                      onDragOver={handleCommentDragOver}
                      onDragLeave={handleCommentDragLeave}
                      onDrop={handleCommentDrop}
                      className={`bg-white border-t transition-all duration-200 ${
                        isDraggingCommentFiles
                          ? 'border-blue-500 bg-blue-50/70 ring-2 ring-blue-500/20'
                          : 'border-slate-200'
                      } shrink-0`}
                    >
                      {isDraggingCommentFiles && (
                        <div className="p-3 bg-blue-100/90 border-b border-blue-200 text-blue-700 text-xs font-semibold flex items-center justify-center gap-2 pointer-events-none animate-pulse">
                          <FiUploadCloud className="text-lg animate-bounce" />
                          <span>Drop file(s) here to attach to comment</span>
                        </div>
                      )}

                      {/* Attached files preview chips */}
                      {commentFiles.length > 0 && (
                        <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex flex-wrap gap-2.5 max-h-36 overflow-y-auto">
                          {commentFiles.map((file, idx) => {
                            const isImg = file.type && file.type.startsWith('image/');
                            const previewUrl = isImg ? URL.createObjectURL(file) : null;

                            return (
                              <div key={idx} className="relative group shrink-0">
                                {isImg ? (
                                  <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-blue-400 bg-slate-100 shadow-xs relative">
                                    <img
                                      src={previewUrl}
                                      alt={file.name}
                                      className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-slate-900/60 text-[9px] text-white px-1 py-0.5 truncate text-center">
                                      {formatFileSize(file.size)}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="h-16 px-3 rounded-xl border border-blue-200 bg-blue-50/80 flex items-center gap-2 text-xs shadow-2xs">
                                    <FaFileAlt className="text-blue-600 text-xl shrink-0" />
                                    <div className="max-w-[110px]">
                                      <p className="truncate font-semibold text-[11px] text-slate-800" title={file.name}>
                                        {file.name}
                                      </p>
                                      <p className="text-[10px] text-slate-500">
                                        {formatFileSize(file.size)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCommentFile(idx)}
                                  className="absolute -top-1.5 -right-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md transition cursor-pointer"
                                  title="Remove attachment"
                                >
                                  <FaTimes className="w-2.5 h-2.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Text Input Row */}
                      <div className="p-4 flex items-center gap-3">
                        <input
                          type="file"
                          ref={commentFileInputRef}
                          onChange={handleCommentFileChange}
                          multiple
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.zip"
                        />
                        <button
                          type="button"
                          onClick={() => commentFileInputRef.current?.click()}
                          title="Attach file / image (Max 5 MB per file)"
                          className="w-11 h-11 rounded-2xl border border-slate-200 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/50 text-slate-600 hover:text-blue-600 flex items-center justify-center transition shadow-2xs cursor-pointer shrink-0"
                        >
                          <FaPaperclip className="text-base" />
                        </button>
                        <textarea
                          rows={2}
                          placeholder="Write a message... (Press Shift+Enter for new line)"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onPaste={handleCommentPaste}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendComment();
                            }
                          }}
                          className="flex-1 p-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition resize-none leading-relaxed"
                        ></textarea>
                        <button
                          onClick={handleSendComment}
                          disabled={isSubmittingComment || (!commentText.trim() && commentFiles.length === 0)}
                          className="w-11 h-11 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white flex items-center justify-center transition shadow-sm shrink-0 cursor-pointer disabled:cursor-not-allowed"
                          title="Send message"
                        >
                          {isSubmittingComment ? (
                            <FaSpinner className="animate-spin text-base" />
                          ) : (
                            <AiOutlineSend className="text-xl" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ========================================================================= */}
      {/* IMAGE PREVIEW LIGHTBOX MODAL                                              */}
      {/* ========================================================================= */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-[90vh] bg-slate-900 rounded-3xl overflow-hidden shadow-2xl flex flex-col border border-white/10">
            <div className="p-4 bg-slate-950/80 text-white flex items-center justify-between border-b border-white/10">
              <span className="text-xs font-semibold truncate max-w-md">{previewFile.name}</span>
              <div className="flex items-center gap-2">
                <a
                  href={previewFile.url}
                  download={previewFile.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-medium text-white transition flex items-center gap-1.5"
                >
                  <FaExternalLinkAlt className="text-xs" />
                  <span>Open Full Size</span>
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center overflow-auto max-h-[calc(90vh-70px)]">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-w-full max-h-[75vh] object-contain rounded-xl"
              />
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* EDIT TICKET MODAL (Admin)                                                 */}
      {/* ========================================================================= */}
      {isAdmin && showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 border border-slate-200/80 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">Edit Ticket</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4 pt-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Product / Cloud Service
                </label>
                <select
                  value={editForm.product_list}
                  onChange={(e) =>
                    setEditForm({ ...editForm, product_list: e.target.value })
                  }
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  {DEFAULT_CLOUD_PRODUCTS.map((prod) => (
                    <option key={prod} value={prod}>
                      {prod}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Issue Summary (Title)
                </label>
                <input
                  type="text"
                  required
                  value={editForm.describe_issue}
                  onChange={(e) =>
                    setEditForm({ ...editForm, describe_issue: e.target.value })
                  }
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Detail Description
                </label>
                <textarea
                  rows={3}
                  value={editForm.detail_issue}
                  onChange={(e) =>
                    setEditForm({ ...editForm, detail_issue: e.target.value })
                  }
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">
                    Priority
                  </label>
                  <select
                    value={editForm.priority}
                    onChange={(e) =>
                      setEditForm({ ...editForm, priority: e.target.value })
                    }
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    {Object.keys(PRIORITY_CONFIG).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm({ ...editForm, status: e.target.value })
                    }
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="Open">Open</option>
                    <option value="On Progress">On Progress</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={editForm.contact}
                  onChange={(e) =>
                    setEditForm({ ...editForm, contact: e.target.value })
                  }
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  CC Emails (comma-separated)
                </label>
                <input
                  type="text"
                  placeholder="engineer1@corp.com, manager@corp.com"
                  value={editForm.cc_emails}
                  onChange={(e) =>
                    setEditForm({ ...editForm, cc_emails: e.target.value })
                  }
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdatingTicket}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition flex items-center gap-1.5 shadow-sm"
                >
                  {isUpdatingTicket ? (
                    <>
                      <FaSpinner className="animate-spin text-xs" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION MODAL (Admin)                                         */}
      {/* ========================================================================= */}
      {isAdmin && showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95 duration-200 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center text-lg">
                <FaTrash />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Delete Ticket</h3>
                <p className="text-xs text-slate-500">This action cannot be undone.</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to delete ticket{' '}
              <span className="font-mono font-bold text-slate-800">{ticket?.ticket_id}</span>? All
              associated comments and attachments will also be permanently removed.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteTicket}
                disabled={isDeletingTicket}
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white transition flex items-center gap-1.5 shadow-sm"
              >
                {isDeletingTicket ? (
                  <>
                    <FaSpinner className="animate-spin text-xs" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Yes, Delete Ticket</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* ERROR / NOTICE MODAL                                                      */}
      {/* ========================================================================= */}
      {errorModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in zoom-in-95 duration-200 space-y-4">
            <div className="flex items-center gap-3 text-amber-600">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center text-lg">
                <FaExclamationCircle />
              </div>
              <h3 className="text-base font-bold text-slate-900">{errorModal.title}</h3>
            </div>
            <p className="text-xs text-slate-600 whitespace-pre-line leading-relaxed">
              {errorModal.message}
            </p>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setErrorModal({ isOpen: false, title: '', message: '' })}
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-900 text-white transition shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
